import React, { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import {
  Documents,
  ExtrasSummary,
  HeightCell,
  LoadSummaryCell,
  WeightCell,
  BusinessCell,
} from 'modules/Common/Details';

import { ButtonRound, Flexer, Link, Spacer, Icon, Text, Tag } from '@ontruck/orc';
import ShipperOrderTag from 'modules/shipper-order/Tag';
import { StyledCardHeader, StyledFrozenTag } from 'modules/shipper-order/Card/styled';
import {
  editOrder,
  repeatOrder,
  showRemoveOrderDecisionModal,
} from 'redux/modules/job-detail/effects';

import { injectIntl } from 'react-intl';

import { IS_TENANT } from 'tenant/config';
import {
  DecaLink,
  getShipperOrderControlDocumentUrl,
  isControlDocumentAvailable,
  openControlDocument,
} from 'modules/journey/control-document';

const Header = ({
  shipperOrder,
  skin,
  withinGroupage,
  isFrozen,
  showRemoveButton,
  journeyId,
  journeyStatus,
  intl,
}) => {
  const dispatch = useDispatch();
  const { pathname } = useLocation();
  const t = intl.messages.modules.shipper_order.header;
  const handleEdit = useCallback(() => {
    dispatch(editOrder(shipperOrder?.id, pathname));
  }, [dispatch, shipperOrder, pathname]);

  const handleRepeat = useCallback(() => {
    dispatch(repeatOrder(shipperOrder?.id));
  }, [dispatch, shipperOrder]);

  const handleRemove = useCallback(() => {
    dispatch(showRemoveOrderDecisionModal({ id: shipperOrder?.id }));
  }, [dispatch, shipperOrder]);

  // Only within a journey (journey detail): the order's DeCA of that journey
  const showControlDocument = !!journeyId && isControlDocumentAvailable(journeyStatus);
  const handleControlDocument = () =>
    openControlDocument(
      () => getShipperOrderControlDocumentUrl(shipperOrder?.id, journeyId),
      t.deca_not_available
    );

  const isLHExpress = shipperOrder?.service_type && shipperOrder?.service_type === 'LH_EXPRESS';

  const { stops = [] } = shipperOrder || {};
  const is_export = IS_TENANT && stops && stops.length > 2 && stops[1].stop_type === 'PICKUP';

  const loadDropoff = stops[0]?.load_dropoff || [];
  const loadPickup = stops[0]?.load_pickup || [];
  const reference_number = (
    [...loadPickup, ...loadDropoff].filter((load) => !!load.reference_number)[0] || {}
  ).reference_number;

  return (
    <StyledCardHeader p="XS" pLeft="S" pRight="S" alignItems="center">
      <Flexer.Child flexGrow="1">
        <Flexer.Parent alignItems="center">
          <Spacer mRight="XS">
            <ShipperOrderTag
              id={shipperOrder?.id}
              status={shipperOrder?.status}
              insuredFor={shipperOrder?.insured_for}
              currency={shipperOrder?.currency}
              shipperOrdersSkin={skin}
              isRecurring={!!shipperOrder?.recurring_order}
              withinGroupage={withinGroupage}
              copiable
            />
          </Spacer>
          <Spacer mRight="S">
            {IS_TENANT ? (
              <Tag
                text={is_export ? t.export : t.import}
                skin={is_export ? 'modernGrey' : 'priority'}
                textTransform="uppercase"
                weight="bold"
                size="M"
              />
            ) : (
              <BusinessCell
                isFtl={shipperOrder?.is_ftl}
                serviceType={shipperOrder?.service_type}
                isHighPrio={shipperOrder?.is_high_priority}
                isLHExpress={isLHExpress}
              />
            )}
          </Spacer>
          <Spacer mRight="XS">
            <LoadSummaryCell {...shipperOrder} showEquivalents />
          </Spacer>
          {IS_TENANT && (
            <Spacer mRight="XS">
              <span>{reference_number}</span>
            </Spacer>
          )}
          <Spacer mRight="XS">{!IS_TENANT && <WeightCell {...shipperOrder} />}</Spacer>
          <Spacer mRight="XS">
            <HeightCell {...shipperOrder} />
          </Spacer>

          <Spacer mRight="XS">
            <ExtrasSummary
              {...shipperOrder}
              config={{
                additionalDocuments: {
                  show: false,
                },
              }}
              small
            />
          </Spacer>
        </Flexer.Parent>
      </Flexer.Child>
      <Flexer.Child>
        <Flexer.Parent alignItems="center">
          {showControlDocument && (
            <Flexer.Child mRight="S">
              <DecaLink
                color="black"
                onClick={handleControlDocument}
                dataE2E="shipper_order_header__deca"
              />
            </Flexer.Child>
          )}
          <Flexer.Child>
            <Documents {...shipperOrder} color="black" />
          </Flexer.Child>
          <Flexer.Child mRight="M">
            <Link text={t.repeat} weight="bold" onClick={handleRepeat} />
          </Flexer.Child>
          <Flexer.Child>
            {isFrozen && (
              <StyledFrozenTag>
                <Icon glyph="frozen" size="1.4rem" />
                &nbsp;
                <Text weight="bold">{t.frozen}</Text>
              </StyledFrozenTag>
            )}
            {!isFrozen && <Link text={t.edit} weight="bold" onClick={handleEdit} />}
          </Flexer.Child>
        </Flexer.Parent>
      </Flexer.Child>
      {showRemoveButton && !isFrozen && (
        <Flexer.Child mLeft="S" data-testid="remove-button">
          <ButtonRound skin="primary" size="XS" onClick={handleRemove} icon="close" />
        </Flexer.Child>
      )}
    </StyledCardHeader>
  );
};

export default injectIntl(Header);
